import { message as Message } from 'ant-design-vue';
import niuConfig from '@/config/niuTrans';
import { batchTranslate, ensureTranslateContext } from './api';
import {
  collectAllTargets,
  applyTargetTranslations,
  applyCachedTranslations,
  clearTranslationMaps,
  loadPersistedCache,
  persistCache,
  getCachedTranslation,
  setCachedTranslation,
  isNearViewport,
} from './dom';

let translating = false;
let translateQueued = false;
let pendingTimer = null;
let pendingTimers = [];
let observer = null;
let observed = false;
let autoEnabled = false;
let maskVisible = false;
let applyingCache = false;
let interactionBound = false;
let overlayTimer = null;
let softPendingCount = 0;
/** 路由切换时递增，旧翻译任务看到后立即停 */
let translateGeneration = 0;
let activeAbort = null;
const maskListeners = new Set();

function isAbortError(err) {
  return !!(err && (err.name === 'AbortError' || err.code === 20));
}

function isStaleGeneration(gen) {
  return gen !== translateGeneration;
}

function getActiveSignal() {
  if (!activeAbort) activeAbort = new AbortController();
  return activeAbort.signal;
}

/**
 * 取消上一轮翻译（定时器 / 进行中的请求 / 排队），用于路由切换
 * @returns {number} 新的 generation
 */
export function cancelPendingTranslate() {
  translateGeneration += 1;
  translateQueued = false;
  translating = false;
  if (activeAbort) {
    try { activeAbort.abort(); } catch (e) { /* ignore */ }
    activeAbort = null;
  }
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  for (let i = 0; i < pendingTimers.length; i++) {
    clearTimeout(pendingTimers[i]);
  }
  pendingTimers = [];
  if (overlayTimer) {
    clearTimeout(overlayTimer);
    overlayTimer = null;
  }
  softPendingCount = 0;
  if (typeof document !== 'undefined') {
    const nodes = document.querySelectorAll('.niu-trans-soft-pending');
    for (let i = 0; i < nodes.length; i++) setSoftPending(nodes[i], false);
  }
  emitMask(false);
  return translateGeneration;
}

/** 常见浮层选择器（antd 下拉/弹层挂到 body） */
const OVERLAY_SELECTOR = [
  '.ant-dropdown',

  '.ant-select-dropdown',
  '.ant-popover',
  '.ant-modal',
  '.ant-drawer',
  '.ant-menu-submenu-popup',
  '.ant-tooltip',
  '.ant-cascader-menus',
].join(',');

/** 同页局部更新：仅允许软藏纯文案区，禁止藏整页（会拖垮 iframe/PDF） */
const SOFT_ROOT_SELECTOR = '.screen';

function setSoftPending(el, on) {
  if (!el || !el.classList) return;
  // 含 iframe/embed 的容器绝不软藏，避免影响业务页
  if (on && typeof el.querySelector === 'function') {
    if (el.querySelector('iframe, embed, object, canvas')) return;
  }
  if (on) el.classList.add('niu-trans-soft-pending');
  else el.classList.remove('niu-trans-soft-pending');
}

function findSoftRootFromNodes(nodes) {
  const list = nodes || [];
  for (let i = 0; i < list.length; i++) {
    const n = list[i];
    const el = n && (n.nodeType === 1 ? n : n.parentElement);
    if (!el || typeof el.closest !== 'function') continue;
    const hit = el.closest(SOFT_ROOT_SELECTOR);
    if (hit) return hit;
  }
  // 不再回退到 .router_view / .standard_main
  return null;
}

/**
 * 局部静默翻译：先隐藏再译再显示，避免筛选项闪中文
 * 可在接口回填后 nextTick 调用
 */
export async function softTranslateElement(root) {
  if (!root) return;
  if (!autoEnabled) {
    setSoftPending(root, false);
    return;
  }
  const gen = translateGeneration;
  softPendingCount += 1;
  setSoftPending(root, true);
  try {
    if (isStaleGeneration(gen)) return;
    await translateSubtree(root);
  } finally {
    softPendingCount = Math.max(0, softPendingCount - 1);
    // 若路由已取消并清过 soft-pending，这里仍安全 remove
    setSoftPending(root, false);
  }
}

/** 进入英文版时预热的高频文案（用户下拉等，打开前就有缓存） */
const PREWARM_TEXTS = [
  '资料设置',
  '我的收藏',
  '我的订单',
  '订单管理',
  '退出登录',
  '登录',
  '注册',
  '提示',
  '确定',
  '取消',
  '搜索',
  '重置',
  '专业',
  '标准来源',
  '状态',
  '标准性质',
  '标签',
  '语种',
  '全部',
  '展开',
  '收起',
  '请输入标准名称',
  '请输入标准编号',
  '请输入ICS分类',
  '请输入CCS编号',
  '请输入起草人',
  '请输入起草单位',
  '请选择语种',
  '请选择年代号',
  '请选择标准来源',
  '请选择标准状态',
  '开始日期',
  '结束日期',
  '开始日期 ~ 结束日期',
  '请输入内容',
  '请输入关键词继续检索',
  '请输入标准关键词',
  '请选择分类进行筛选',
  '请输入对标点名称关键字搜索',
  '请选择项目类别',
  '请选择所在地区',
  '查看体系图',
  '温馨提示：该内容为本站VIP会员专享，升级VIP请联系客服。服务热线:027-87665517转6003。邮箱: userservice@sunther.com',
];

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** 按条数 + 总字符数拆批，避免“单次请求字符数超过最大限制” */
function chunkByLimit(texts, maxCount, maxChars) {
  const groups = [];
  let current = [];
  let chars = 0;
  for (let i = 0; i < texts.length; i++) {
    const t = String(texts[i] || '');
    const len = t.length;
    // 单条过长时单独成批（接口若仍拒，后续会跳过并保留原文）
    if (current.length && (current.length >= maxCount || chars + len > maxChars)) {
      groups.push(current);
      current = [];
      chars = 0;
    }
    current.push(t);
    chars += len;
    if (current.length >= maxCount || chars >= maxChars) {
      groups.push(current);
      current = [];
      chars = 0;
    }
  }
  if (current.length) groups.push(current);
  return groups;
}

function isCharLimitError(err) {
  const msg = (err && err.message) || String(err || '');
  return /字符数|最大限制|too large|too long|limit/i.test(msg);
}

/** 长文按句/长度切开，避免单条超限被整段跳过 */
function splitLongText(text, maxChars) {
  const src = String(text || '');
  if (src.length <= maxChars) return [src];
  const parts = [];
  const pieces = src.split(/([。！？；\n.!?;]+)/);
  let buf = '';
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i] || '';
    if (!piece) continue;
    if ((buf + piece).length > maxChars && buf) {
      parts.push(buf);
      buf = piece;
    } else {
      buf += piece;
    }
  }
  if (buf) parts.push(buf);

  const out = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.length <= maxChars) {
      out.push(p);
    } else {
      for (let j = 0; j < p.length; j += maxChars) out.push(p.slice(j, j + maxChars));
    }
  }
  return out.length ? out : [src.slice(0, maxChars)];
}

async function translateLongText(text, auth, gen) {
  const maxChars = Math.max(500, (niuConfig.maxBatchChars || 2000) - 100);
  const parts = splitLongText(text, maxChars);
  const translatedParts = [];
  for (let i = 0; i < parts.length; i++) {
    if (isStaleGeneration(gen)) break;
    try {
      const arr = await batchTranslate([parts[i]], {
        auth: auth,
        signal: getActiveSignal(),
      });
      const one = arr && arr[0];
      translatedParts.push(one && one !== parts[i] ? one : parts[i]);
    } catch (e) {
      if (isAbortError(e) || isStaleGeneration(gen)) break;
      translatedParts.push(parts[i]);
    }
  }
  return translatedParts.join('');
}

function emitMask(visible) {
  maskVisible = !!visible;
  maskListeners.forEach(function (fn) {
    try { fn(maskVisible); } catch (e) { /* ignore */ }
  });
}

function wait(ms) {
  return new Promise(function (resolve) { setTimeout(resolve, ms); });
}

export function onNiuTransMask(fn) {
  if (typeof fn !== 'function') return function () {};
  maskListeners.add(fn);
  fn(maskVisible);
  return function () { maskListeners.delete(fn); };
}

export function getNiuTransMask() {
  return maskVisible;
}

/** 同步套用缓存，尽量在绘制前把中文换成英文 */
export function flushCacheNow() {
  if (!autoEnabled || applyingCache) return 0;
  applyingCache = true;
  try {
    loadPersistedCache();
    return applyCachedTranslations();
  } finally {
    Promise.resolve().then(function () { applyingCache = false; });
  }
}

function hasRemainingChinese() {
  const left = collectAllTargets(null, { onlyCjk: true });
  return left.items && left.items.length > 0;
}

function hasViewportChinese() {
  const left = collectAllTargets(null, { onlyCjk: true });
  if (!left.items || !left.items.length) return false;
  for (let i = 0; i < left.items.length; i++) {
    const item = left.items[i];
    const node = item.type === 'text' ? item.node : item.el;
    if (isNearViewport(node)) return true;
  }
  return false;
}

/** DOM/点击后：先缓存回填，仍有中文再短延迟补翻（默认同页静默，不开整页遮罩） */
function handleDomSettled(options) {
  // 仅显式 withMask:true 才整页遮罩；菜单/筛选取数等局部更新一律静默
  const needMask = !!(options && options.withMask);
  const softRoot = options && options.softRoot;
  if (!autoEnabled || translating) return;
  flushCacheNow();
  if (!hasRemainingChinese()) return;

  const delay = needMask
    ? (niuConfig.routeDelay != null ? niuConfig.routeDelay : 200)
    : (niuConfig.observerDelay != null ? niuConfig.observerDelay : 250);
  scheduleTranslate(delay, {
    withMask: needMask,
    softRoot: needMask ? null : softRoot,
  });
}

async function collectChineseNodes() {
  loadPersistedCache();
  applyCachedTranslations();
  let result = collectAllTargets(null, { onlyCjk: true });
  if (result.items.length) return result;
  await wait(niuConfig.collectRetryDelay != null ? niuConfig.collectRetryDelay : 100);
  applyCachedTranslations();
  result = collectAllTargets(null, { onlyCjk: true });
  return result;
}

async function mapPool(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  async function run() {
    while (cursor < items.length) {
      const i = cursor;
      cursor += 1;
      results[i] = await worker(items[i], i);
    }
  }
  const runners = [];
  const n = Math.min(limit, items.length) || 1;
  for (let i = 0; i < n; i++) runners.push(run());
  await Promise.all(runners);
  return results;
}

async function translateUniqueTexts(texts, auth, gen) {
  const runGen = gen != null ? gen : translateGeneration;
  const unique = [];
  const seen = Object.create(null);

  for (let i = 0; i < texts.length; i++) {
    const t = texts[i];
    if (getCachedTranslation(t)) continue;
    if (seen[t] == null) {
      seen[t] = unique.length;
      unique.push(t);
    }
  }

  if (!unique.length) {
    return texts.map(function (t) { return getCachedTranslation(t) || t; });
  }

  const batchSize = niuConfig.batchSize || 15;
  const maxChars = niuConfig.maxBatchChars || 2000;
  const concurrency = niuConfig.concurrency || 3;
  const groups = chunkByLimit(unique, batchSize, maxChars);

  async function translateOneGroup(group) {
    if (isStaleGeneration(runGen)) return;
    try {
      // 单条过长先拆句翻译，再写入缓存
      if (group.length === 1 && String(group[0] || '').length > maxChars) {
        const src = group[0];
        const tgt = await translateLongText(src, auth, runGen);
        if (!isStaleGeneration(runGen) && tgt && tgt !== src) setCachedTranslation(src, tgt);
        return;
      }
      const translated = await batchTranslate(group, {
        auth: auth,
        signal: getActiveSignal(),
      });
      if (isStaleGeneration(runGen)) return;
      for (let j = 0; j < group.length; j++) {
        const src = group[j];
        const tgt = translated[j];
        if (tgt && tgt !== src) setCachedTranslation(src, tgt);
      }
    } catch (err) {
      if (isAbortError(err) || isStaleGeneration(runGen)) return;
      if (isCharLimitError(err) && group.length > 1) {
        const mid = Math.ceil(group.length / 2);
        await translateOneGroup(group.slice(0, mid));
        await translateOneGroup(group.slice(mid));
        return;
      }
      if (isCharLimitError(err) && group.length === 1) {
        const src = group[0];
        const tgt = await translateLongText(src, auth, runGen);
        if (!isStaleGeneration(runGen) && tgt && tgt !== src) setCachedTranslation(src, tgt);
        return;
      }
      throw err;
    }
  }

  await mapPool(groups, concurrency, async function (group) {
    if (isStaleGeneration(runGen)) return false;
    await translateOneGroup(group);
    return true;
  });

  return texts.map(function (t) { return getCachedTranslation(t) || t; });
}

async function translateNodeGroup(items, auth, gen) {
  if (!items.length) return;
  if (isStaleGeneration(gen != null ? gen : translateGeneration)) return;
  const texts = items.map(function (x) { return x.text; });
  const translated = await translateUniqueTexts(texts, auth, gen);
  if (isStaleGeneration(gen != null ? gen : translateGeneration)) return;
  applyTargetTranslations(items, translated);
}

/** 只翻译某个子树（下拉/弹窗/表单区），含 placeholder 等属性 */
export async function translateSubtree(root) {
  if (!root || !autoEnabled) return;
  const gen = translateGeneration;
  loadPersistedCache();
  applyCachedTranslations(root);
  const collected = collectAllTargets(root, { onlyCjk: true });
  if (!collected.items.length) return;
  try {
    const auth = await ensureTranslateContext();
    if (isStaleGeneration(gen)) return;
    await translateNodeGroup(collected.items, auth, gen);
    if (isStaleGeneration(gen)) return;
    persistCache();
  } catch (e) {
    if (isAbortError(e) || isStaleGeneration(gen)) return;
    /* 浮层补翻失败不打断主流程 */
  }
}

function collectOverlayRoots(fromNodes) {
  const roots = [];
  const push = function (el) {
    if (el && el.nodeType === 1 && roots.indexOf(el) === -1) roots.push(el);
  };
  const list = fromNodes || [];
  for (let i = 0; i < list.length; i++) {
    const n = list[i];
    if (!n || n.nodeType !== 1) continue;
    if (typeof n.matches === 'function' && n.matches(OVERLAY_SELECTOR)) push(n);
    if (n.classList && (n.classList.contains('ant-dropdown-menu') || n.classList.contains('antd_menu'))) {
      push(n);
    }
    if (typeof n.querySelectorAll === 'function') {
      const found = n.querySelectorAll(OVERLAY_SELECTOR + ', .ant-dropdown-menu, .antd_menu');
      for (let j = 0; j < found.length; j++) push(found[j]);
    }
  }
  return roots;
}

function translateVisibleOverlays() {
  if (!autoEnabled || typeof document === 'undefined') return;
  flushCacheNow();
  const overlays = document.querySelectorAll(OVERLAY_SELECTOR);
  for (let i = 0; i < overlays.length; i++) {
    const el = overlays[i];
    // 不可见的跳过
    if (el.offsetParent === null && !el.classList.contains('ant-dropdown')) {
      const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden')) continue;
    }
    translateSubtree(el);
  }
}

function scheduleOverlayTranslate(roots) {
  if (overlayTimer) clearTimeout(overlayTimer);
  overlayTimer = setTimeout(function () {
    overlayTimer = null;
    const list = roots && roots.length ? roots : null;
    if (list) {
      list.forEach(function (root) { translateSubtree(root); });
    } else {
      translateVisibleOverlays();
    }
  }, 30);
}

/** 预热下拉等常用中文，打开时直接命中缓存 */
async function prewarmCommonTexts() {
  loadPersistedCache();
  const need = PREWARM_TEXTS.filter(function (t) { return !getCachedTranslation(t); });
  if (!need.length) return;
  try {
    const auth = await ensureTranslateContext();
    await translateUniqueTexts(need, auth);
    persistCache();
  } catch (e) { /* ignore */ }
}

export async function translatePage(options) {
  const silent = options && options.silent;
  const withMask = options && options.withMask;
  const gen = translateGeneration;
  // 同 generation 内若已在翻，排队；路由取消后 translating 已被清掉，可立刻开新一轮
  if (translating) {
    translateQueued = true;
    if (withMask) emitMask(true);
    return;
  }
  translating = true;
  activeAbort = new AbortController();
  if (withMask) emitMask(true);
  try {
    if (isStaleGeneration(gen)) return;
    const auth = await ensureTranslateContext();
    if (isStaleGeneration(gen)) return;
    const collected = await collectChineseNodes();
    if (isStaleGeneration(gen)) return;
    const items = collected.items || [];
    if (!items.length) {
      if (!silent) {
        Message.success({ content: '已切换为英文版', key: 'niuTrans', duration: 1 });
      }
      return;
    }

    const first = [];
    const rest = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const probe = item.type === 'text' ? item.node : item.el;
      if (isNearViewport(probe)) first.push(item);
      else rest.push(item);
    }

    if (!silent) Message.loading({ content: 'Loading English…', key: 'niuTrans', duration: 0 });

    if (first.length) {
      await translateNodeGroup(first, auth, gen);
      if (isStaleGeneration(gen)) return;
      persistCache();
      if (withMask) emitMask(false);
      if (!silent) Message.success({ content: '已切换为英文版', key: 'niuTrans', duration: 1.2 });
      if (rest.length) {
        await translateNodeGroup(rest, auth, gen);
        if (isStaleGeneration(gen)) return;
        persistCache();
      }
    } else {
      await translateNodeGroup(items, auth, gen);
      if (isStaleGeneration(gen)) return;
      persistCache();
      if (withMask) emitMask(false);
      if (!silent) Message.success({ content: '已切换为英文版', key: 'niuTrans', duration: 1.2 });
    }
  } catch (err) {
    if (isAbortError(err) || isStaleGeneration(gen)) return;
    // 字符超限已在批次内自动拆分重试；这里仅提示通用英文，避免弹中文报错影响观感
    if (!silent) {
      if (isCharLimitError(err)) {
        Message.success({ content: '已切换为英文版', key: 'niuTrans', duration: 1.2 });
      } else {
        Message.error({ content: (err && err.message) || 'Translation failed', key: 'niuTrans', duration: 5 });
      }
    }
  } finally {
    // 仅本轮仍有效时收尾；已被路由取消则交给新一轮
    if (!isStaleGeneration(gen)) {
      translating = false;
      if (withMask) emitMask(false);
      if (translateQueued) {
        translateQueued = false;
        scheduleTranslate(80, { withMask: false, debounce: false });
      }
    }
  }
}

export function restorePage(options) {
  const clearCache = options && options.clearCache;
  if (clearCache) clearTranslationMaps();
  emitMask(false);
  Message.success('已切换为中文版');
}

export function scheduleTranslate(delay, options) {
  const waitMs = delay != null ? delay : (niuConfig.routeDelay || 200);
  const withMask = !!(options && options.withMask);
  // debounce 默认 true；列表补翻传 debounce:false，避免互相取消
  const debounce = !(options && options.debounce === false);
  const softRoot = !withMask && options && options.softRoot ? options.softRoot : null;
  const gen = translateGeneration;
  flushCacheNow();
  if (withMask) emitMask(true);
  if (softRoot) {
    softPendingCount += 1;
    setSoftPending(softRoot, true);
  }
  if (debounce && pendingTimer) clearTimeout(pendingTimer);
  return new Promise(function (resolve) {
    const timer = setTimeout(async function () {
      if (debounce) pendingTimer = null;
      pendingTimers = pendingTimers.filter(function (t) { return t !== timer; });
      if (isStaleGeneration(gen)) {
        if (softRoot) {
          softPendingCount = Math.max(0, softPendingCount - 1);
          setSoftPending(softRoot, false);
        }
        resolve();
        return;
      }
      flushCacheNow();
      try {
        await translatePage({ silent: true, withMask: false });
      } catch (e) {
        /* ignore */
      } finally {
        if (withMask && !isStaleGeneration(gen)) emitMask(false);
        if (softRoot) {
          softPendingCount = Math.max(0, softPendingCount - 1);
          setSoftPending(softRoot, false);
        }
      }
      resolve();
    }, waitMs);
    if (debounce) pendingTimer = timer;
    else pendingTimers.push(timer);
  });
}

function onUserInteract() {
  if (!autoEnabled || translating) return;
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      handleDomSettled({ withMask: false });
      // 点击后可能刚打开下拉，专门扫一遍浮层
      scheduleOverlayTranslate();
    });
  });
}

function onOverlayHover(e) {
  if (!autoEnabled || translating) return;
  const t = e.target;
  if (!t || typeof t.closest !== 'function') return;
  // 仅在可能打开下拉的区域触发，避免全局 mouseenter 过重
  if (!t.closest('.user, .header, .navigation, .ant-dropdown-trigger, .ant-select, .ant-dropdown')) return;
  requestAnimationFrame(function () {
    scheduleOverlayTranslate();
  });
}

function bindInteractionHooks() {
  if (interactionBound || typeof document === 'undefined') return;
  interactionBound = true;
  document.addEventListener('click', onUserInteract, true);
  document.addEventListener('change', onUserInteract, true);
  // 鼠标移入用户头像/下拉触发器时提前补翻
  document.addEventListener('mouseenter', onOverlayHover, true);
}

function unbindInteractionHooks() {
  if (!interactionBound || typeof document === 'undefined') return;
  interactionBound = false;
  document.removeEventListener('click', onUserInteract, true);
  document.removeEventListener('change', onUserInteract, true);
  document.removeEventListener('mouseenter', onOverlayHover, true);
}

export function startAutoTranslateObserver() {
  autoEnabled = true;
  bindInteractionHooks();
  if (observed || typeof MutationObserver === 'undefined') return;
  observed = true;
  observer = new MutationObserver(function (mutations) {
    if (!autoEnabled || applyingCache) return;

    const added = [];
    for (let i = 0; i < mutations.length; i++) {
      const nodes = mutations[i].addedNodes;
      for (let j = 0; j < nodes.length; j++) added.push(nodes[j]);
    }

    // 优先处理新挂载的下拉/弹层
    const overlays = collectOverlayRoots(added);
    if (overlays.length) {
      flushCacheNow();
      scheduleOverlayTranslate(overlays);
      return;
    }

    if (translating || maskVisible || softPendingCount > 0) return;
    flushCacheNow();
    if (!hasRemainingChinese()) return;
    // 列表/菜单/筛选取数：软隐藏局部容器再补翻，避免闪中文、也不开整页遮罩
    handleDomSettled({
      withMask: false,
      softRoot: findSoftRootFromNodes(added),
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: false
  });
}

export function stopAutoTranslateObserver() {
  autoEnabled = false;
  unbindInteractionHooks();
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  observed = false;
  cancelPendingTranslate();
}

export async function switchSiteLanguage(lang, options) {
  const store = options && options.store;
  const force = options && options.force;
  if (lang === 'english') {
    if (store) store.commit('setLanguage', 'english');
    if (force) clearTranslationMaps();
    loadPersistedCache();
    startAutoTranslateObserver();
    await wait(30);
    // 先预热下拉等高频文案，再整页翻译
    prewarmCommonTexts();
    await translatePage({ silent: false, withMask: true });
    prewarmCommonTexts();
    return;
  }
  if (store) store.commit('setLanguage', 'chinese');
  stopAutoTranslateObserver();
  clearTranslationMaps();
  emitMask(false);
  window.location.reload();
}

export function bootstrapNiuTrans(store) {
  const lang = store && store.state && store.state.language;
  if (lang === 'english') {
    loadPersistedCache();
    startAutoTranslateObserver();
    flushCacheNow();
    prewarmCommonTexts();
    scheduleTranslate(niuConfig.routeDelay || 200, { withMask: true });
  }
}
